import React, { memo } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DirectionsWalkIcon from "../../icons/DirectionsWalkIcon";
import SixMinuteWalkRouteMap from "../../charts/SixMinuteWalkRouteMap";
import { useSixMinuteWalkTracking } from "../../../hooks/useSixMinuteWalkTracking";
import { SIX_MINUTE_WALK_BASE_PATH } from "../../../utils/recording/sixMinuteWalkConfig";

// Preserve the pre-start single-location framing while early GPS samples are
// still too close together for fitBounds to produce a useful route view.
const LIVE_WALK_MAP_MAX_ZOOM = 17;

function SixMinuteWalkProgressBar({ isReturning, progressPercent }) {
  return (
    <div className="flex items-end gap-3">
      <span
        aria-hidden="true"
        className="material-symbols-outlined mb-0.5 shrink-0 text-[1.9rem] leading-none text-secondary"
      >
        home_pin
      </span>

      <div className="relative flex-1 pt-8">
        <DirectionsWalkIcon
          className="absolute top-0 !h-7 !w-7 text-primary transition-[left,transform] duration-200"
          style={{
            left: `${progressPercent}%`,
            transform: `translateX(-50%)${isReturning ? " scaleX(-1)" : ""}`,
          }}
        />

        <div className="h-3.5 w-full overflow-hidden rounded-full bg-greyscale-1">
          <div
            className="h-full bg-primary transition-[width] duration-200"
            style={{
              width: `${progressPercent}%`,
            }}
          />
        </div>
      </div>

      <span
        aria-hidden="true"
        className="material-symbols-outlined mb-0.5 shrink-0 text-[1.9rem] leading-none text-secondary"
      >
        flag_2
      </span>
    </div>
  );
}

function SixMinuteWalkMetricsCard({
  highlightedDistanceLabel,
  isTimedComplete,
  isReturning,
  locationError,
  progressPercent,
  timerLabel,
  timerValue,
  t,
  actions = null,
  className = "",
}) {
  return (
    <div
      className={["card flex flex-col gap-4 p-4 text-left", className].join(" ")}
      data-testid="six-minute-walk-metrics-card"
    >
      <SixMinuteWalkProgressBar
        isReturning={isReturning}
        progressPercent={progressPercent}
      />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="muted-text text-xs uppercase tracking-wide">
            {timerLabel}
          </p>
          <p className="text-3xl font-semibold text-primary">{timerValue}</p>
        </div>
        <div>
          <p className="muted-text text-xs uppercase tracking-wide">
            {t(
              isTimedComplete
                ? "result.sixMinuteWalk.sixMinuteDistanceLabel"
                : "result.sixMinuteWalk.distanceLabel",
              isTimedComplete ? "6-minute distance" : "Distance",
            )}
          </p>
          <p className="text-3xl font-semibold text-primary">
            {highlightedDistanceLabel}{" "}
            <span className="text-base font-medium text-greyscale-3">m</span>
          </p>
        </div>
      </div>

      {locationError ? (
        <p className="text-sm text-red-700">
          {locationError === "unsupported"
            ? t(
                "result.sixMinuteWalk.locationUnsupported",
                "Location services are not available on this device.",
              )
            : t(
                "result.sixMinuteWalk.locationError",
                "We couldn't access your location. Please allow location access and try again.",
              )}
        </p>
      ) : null}

      {actions ? <div className="flex flex-col gap-3 pt-1">{actions}</div> : null}
    </div>
  );
}

export default memo(function SixMinuteWalkWalkStage({
  assessment,
  setAssessment,
  audioMuted = false,
  resumePostRecording = false,
  onContinueToPost = null,
}) {
  const { t, i18n } = useTranslation();
  const {
    cancelAssessment,
    geoRouteTracePreview,
    hasPreMeasurement,
    hasStarted,
    highlightedDistanceLabel,
    isReturning,
    isTimedComplete,
    locationError,
    progressPercent,
    proceedToPostRecording,
    routeTracePreview,
    startWalk,
    status,
    timerValue,
  } = useSixMinuteWalkTracking({
    assessment,
    setAssessment,
    audioMuted,
    resumePostRecording,
    onContinueToPost,
    midpointPrompt: t(
      "result.sixMinuteWalk.turnAroundPrompt",
      "Three minutes completed. Please turn around and head back now.",
    ),
    promptLanguage: i18n.resolvedLanguage || i18n.language,
  });

  if (!hasPreMeasurement) {
    return <Navigate to={SIX_MINUTE_WALK_BASE_PATH} replace />;
  }

  const timerLabel = resumePostRecording
    ? t("result.sixMinuteWalk.summaryTimeToRecordingLabel", "Time to recording")
    : t("result.sixMinuteWalk.timerLabel", "Timer");
  const walkStageTitle = isTimedComplete
    ? t("result.sixMinuteWalk.stageCompleteTitle", "Proceed to next step")
    : hasStarted
      ? isReturning
        ? t("result.sixMinuteWalk.stageReturnTitle", "Walk back")
        : t("result.sixMinuteWalk.stageOutTitle", "Walk out")
      : t("result.sixMinuteWalk.stageStartTitle", "Start walk");
  const walkStageBody = isTimedComplete
    ? t(
        "result.sixMinuteWalk.walkCompleteBody",
        "Walk complete. Set yourself up as before, then continue to the post-walk recording.",
      )
    : isReturning
      ? t(
          "result.sixMinuteWalk.walkReturnBody",
          "Walk back to your starting point and set up for your next recording.",
        )
      : t(
          "result.sixMinuteWalk.walkBody",
          "Walk away from your starting point and turn around at 3 minutes.",
        );
  const actionContent = (
    <>
      {!hasStarted || status === "error" ? (
        <button
          type="button"
          className="primary-button w-full"
          onClick={startWalk}
        >
          {t("result.sixMinuteWalk.startWalk", "Start walk")}
        </button>
      ) : null}

      {isTimedComplete ? (
        <button
          type="button"
          className="primary-button w-full"
          onClick={proceedToPostRecording}
        >
          {t("result.sixMinuteWalk.continueButton", "Continue")}
        </button>
      ) : null}

      {hasStarted ? (
        <div className="flex justify-end">
          <button
            type="button"
            className="small-outlined-button"
            onClick={cancelAssessment}
          >
            {t("result.sixMinuteWalk.cancelAssessment", "Cancel")}
          </button>
        </div>
      ) : null}
    </>
  );
  const floatingCardClassName = "card shadow-lg";

  return (
    <div
      className="relative h-full min-h-0 w-full overflow-hidden"
      data-testid="six-minute-walk-stage"
    >
      <div className="absolute inset-0" data-testid="six-minute-walk-stage-map-layer">
        <SixMinuteWalkRouteMap
          geoPoints={geoRouteTracePreview}
          fallbackPoints={routeTracePreview}
          ariaLabel={t(
            "result.sixMinuteWalk.liveRouteAriaLabel",
            "Live walk route preview",
          )}
          height="100%"
          className="h-full w-full rounded-none"
          fillContainer
          showEmptyState
          showCurrentLocationIcon
          maxZoom={LIVE_WALK_MAP_MAX_ZOOM}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/78 via-white/18 to-white/82" />
      </div>

      <div className="relative z-10 mx-auto flex h-full min-h-0 w-full max-w-lg flex-col gap-4 overflow-y-auto px-4 pb-2">
        <section
          className={`${floatingCardClassName} mt-1 p-4 text-left`}
          data-testid="six-minute-walk-stage-summary-card"
        >
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="subtitle-text">{walkStageTitle}</h1>
            <p>{walkStageBody}</p>
          </div>
        </section>

        <div className="mt-auto flex flex-col gap-4">
          <SixMinuteWalkMetricsCard
            highlightedDistanceLabel={highlightedDistanceLabel}
            isTimedComplete={isTimedComplete}
            isReturning={isReturning}
            locationError={locationError}
            progressPercent={progressPercent}
            timerLabel={timerLabel}
            timerValue={timerValue}
            t={t}
            actions={actionContent}
            className={floatingCardClassName}
          />
        </div>
      </div>
    </div>
  );
});
